'use client';

import React, { useState } from 'react';
import { MessageCircle, ClipboardList, X, ChevronRight } from 'lucide-react';
import type { EventData } from '../types/events';
import { buildWhatsAppUrl } from './CarteleraClient';

interface EventCardProps {
  event: EventData;
  onListaFree: (event: EventData) => void;
}

export default function EventCard({ event, onListaFree }: EventCardProps) {
  const [showOptions, setShowOptions] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handleMesasWA = () => {
    let msg = '';
    if (event.venue === 'mdq') {
      msg = `Hola Benja! Quiero consultar por Mesas / Combos para ${event.title} - ${event.venueName} (${event.dayLabel} ${event.dateLabel}) 🙌`;
    } else {
      msg = `Hola Benja! Quiero consultar por Mesas / Combos para ${event.title} (${event.dayLabel} ${event.dateLabel}) 🙌`;
    }
    window.open(buildWhatsAppUrl(msg), '_blank');
    setShowOptions(false);
  };

  const handleListaFree = () => {
    onListaFree(event);
    setShowOptions(false);
  };

  const isMdq = event.venue === 'mdq';

  return (
    <>
      {/* Horizontal Row Card */}
      <div
        className={`${event.gradientClass} card-shine rounded-2xl overflow-hidden transition-all duration-300 w-full`}
        style={{
          border: `1.5px solid ${event.borderColor}`,
          boxShadow: isPressed
            ? `0 0 30px ${event.glowColor}, 0 0 60px ${event.glowColor}`
            : `0 0 18px ${event.glowColor}, 0 0 4px ${event.glowColor}`,
          '--card-glow': event.glowColor,
        } as React.CSSProperties}
        onMouseEnter={() => setIsPressed(true)}
        onMouseLeave={() => setIsPressed(false)}
        onTouchStart={() => setIsPressed(true)}
        onTouchEnd={() => setIsPressed(false)}
      >
        {/* Featured Banner — neon glow */}
        {event.featuredBanner && (
          <div
            className={`featured-banner px-3 py-2 text-center font-bold tracking-wide text-xs sm:text-sm`}
            style={{
              color: '#fff',
              textShadow: `0 0 8px ${event.accentColor}, 0 0 16px ${event.accentColor}`,
              background: `linear-gradient(90deg, rgba(0,0,0,0.4), ${event.glowColor}, rgba(0,0,0,0.4))`,
              border: `1.5px solid ${event.accentColor}`,
              boxShadow: `0 0 14px ${event.glowColor}, inset 0 0 10px ${event.glowColor}`,
            }}
          >
            {event.featuredBanner}
          </div>
        )}

        <div className="flex items-stretch gap-0">
          {/* LEFT — Date block */}
          <div className="flex flex-col items-center justify-center px-3 sm:px-4 py-4 sm:py-5 min-w-[64px] sm:min-w-[76px] bg-black/30 self-stretch">
            <span className="text-[9px] sm:text-[10px] font-bold text-white/60 uppercase tracking-widest leading-none mb-1">
              {event.dayLabel.slice(0, 3).toUpperCase()}
            </span>
            <span
              className="text-3xl sm:text-4xl font-black text-white leading-none"
              style={{ textShadow: `0 0 10px ${event.accentColor}` }}
            >
              {event.dateLabel.split('/')[0]}
            </span>
            <span className="text-[9px] sm:text-[10px] font-semibold text-white/50 leading-none mt-1">
              /{event.dateLabel.split('/')[1]}
            </span>
          </div>

          {/* CENTER — Event info */}
          <div className="flex-1 px-3 sm:px-4 py-3 sm:py-4 min-w-0">
            <h3
              className="text-sm sm:text-base font-extrabold text-white leading-tight"
              style={{ textShadow: `0 0 8px ${event.accentColor}`, wordBreak: 'break-word' }}
            >
              {event.title}
            </h3>
            <p className="text-xs sm:text-sm text-white/80 mt-0.5 leading-snug">
              {event.subtitle}
            </p>
            {event.djLine && (
              <p className="text-xs text-white/60 mt-1.5 leading-snug">🎧 {event.djLine}</p>
            )}
            {event.timeRange && (
              <p className="text-xs text-white/60 mt-1">⏰ {event.timeRange}</p>
            )}
            {event.address && (
              <p className="text-xs text-white/60 mt-1">📍 {event.address}</p>
            )}
            {event.womenOnly && (
              <span className="inline-block mt-2 badge-women-only rounded-full px-2 py-0.5 text-[10px] font-bold">
                👩 Lista Free solo mujeres
              </span>
            )}
          </div>

          {/* RIGHT — Single neon button */}
          <div className="flex items-center justify-center px-2 sm:px-3 py-3 sm:py-4 self-stretch">
            <button
              onClick={() => setShowOptions(true)}
              className="flex flex-col items-center justify-center gap-1 rounded-xl text-white font-bold text-[10px] sm:text-[11px] text-center leading-tight active:scale-95 transition-all"
              style={{
                background: `linear-gradient(135deg, rgba(0,0,0,0.5), rgba(0,0,0,0.3))`,
                border: `1.5px solid ${event.accentColor}`,
                boxShadow: `0 0 16px ${event.glowColor}, 0 0 30px ${event.glowColor}, inset 0 0 10px rgba(255,255,255,0.05)`,
                color: event.accentColor,
                textShadow: `0 0 8px ${event.accentColor}`,
                minWidth: '64px',
                padding: '10px 8px',
              }}
            >
              <ChevronRight size={16} style={{ color: event.accentColor }} />
              <span>TOCÁ</span>
              <span>PARA VER</span>
              <span>OPCIONES</span>
            </button>
          </div>
        </div>
      </div>

      {/* Options Modal */}
      {showOptions && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setShowOptions(false); }}
        >
          <div className="bg-card w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl border border-border animate-slide-up overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="font-extrabold text-sm text-foreground">{event.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {event.dayLabel} {event.dateLabel} · Elegí una opción
                </p>
              </div>
              <button
                onClick={() => setShowOptions(false)}
                className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <X size={18} />
              </button>
            </div>

            {/* Options */}
            <div className="p-4 flex flex-col gap-3">
              {!event.noListaFree && (
                <button
                  onClick={handleListaFree}
                  className="btn-lista-free w-full flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-bold text-white active:scale-95 transition-all"
                >
                  <ClipboardList size={16} />
                  📋 Anotarme en Lista Free
                </button>
              )}
              <button
                onClick={handleMesasWA}
                className="btn-whatsapp w-full flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-bold text-white active:scale-95 transition-all"
              >
                <MessageCircle size={16} />
                🥂 Mesas / Combos (WhatsApp)
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}